import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { RolesPipe } from '@wilson/authorization';
import {
  Activity,
  PublicationStatus,
  Shift,
  ZOOM_LEVEL,
} from '@wilson/interfaces';
import {
  FetchProfitabilityScoreSetting,
  ProfitabilityScoreState,
} from '@wilson/profitability-score/state';
import { ModalInteractionService } from '@wilson/shift-timeline/core';
import { FindShiftStaffPopupComponent } from '@wilson/shift-timeline/modal';
import {
  ResetShiftAssignmentModalState,
  ResetShiftTimelineWidth,
  ResetTimelineInteractionsState,
  SetShiftToAssign,
  SetZoomLevel,
  ShiftAssignmentModalState,
  ToggleRenderTimelineInModal,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { Observable, combineLatest, firstValueFrom, map, of, take } from 'rxjs';
import { ReloadSignal } from './assign-activity-popup-manager.service';

@Injectable()
export class AssignShiftPopupManagerService {
  private readonly translate = inject(TranslateService);
  private readonly nzModalService = inject(NzModalService);
  private readonly store = inject(Store);
  private readonly shiftsService = inject(ShiftsService);
  private readonly nzMessageService = inject(NzMessageService);
  modalInteractionService = inject(ModalInteractionService);
  rolesPipe = inject(RolesPipe);

  hasCompanyAdminRole$: Observable<boolean> = of(false);
  hasViewRoles$: Observable<boolean> = of(false);
  isEnabledProfitabilityScores$ = this.store.select(
    ProfitabilityScoreState.enableSettings,
  );

  public async openFindStaffPopup(shift: Shift & { activities: Activity[] }) {
    this.store.dispatch([
      new ToggleRenderTimelineInModal(),
      new SetZoomLevel(ZOOM_LEVEL.DEFAULT),
      new SetShiftToAssign({
        shift,
      }),
    ]);

    this.hasCompanyAdminRole$ = this.rolesPipe.transform(['company-admin']);
    this.hasViewRoles$ = this.rolesPipe.transform([
      'dispatcher-personnel',
      'dispatcher-operations',
    ]);

    const subscription = combineLatest([
      this.hasCompanyAdminRole$,
      this.hasViewRoles$,
      this.isEnabledProfitabilityScores$,
    ])
      .pipe(
        map(
          ([
            hasCompanyAdminRole,
            hasViewRoles,
            isEnabledProfitabilityScores,
          ]) => {
            if (
              isEnabledProfitabilityScores &&
              (hasCompanyAdminRole || hasViewRoles)
            ) {
              this.store.dispatch(new FetchProfitabilityScoreSetting());
            }
          },
        ),
      )
      .subscribe();

    const modal: NzModalRef = this.nzModalService.create({
      nzTitle: this.translate.instant('page.shift.assign_staff_visual'),
      nzContent: FindShiftStaffPopupComponent,
      nzWidth: '98%',
      nzClosable: false,
      nzFooter: [
        {
          label: this.translate.instant('general.cancel'),
          type: 'default',
          onClick: () => {
            subscription?.unsubscribe();
            modal.destroy();
          },
        },
        {
          label: this.translate.instant('page.services.assign'),
          type: 'primary',
          disabled: () =>
            this.modalInteractionService.selectedRowIndex === null,
          onClick: async () => {
            const userId = this.modalInteractionService.selectedUserId;
            const shiftAssignment = this.store.selectSnapshot(
              ShiftAssignmentModalState.userShiftAssignment(userId),
            );
            if (shiftAssignment) {
              try {
                await firstValueFrom(
                  this.shiftsService.updateShift({
                    ...shiftAssignment.shift,
                    id: shiftAssignment.shift.id,
                    userId: userId,
                    publicationStatus: PublicationStatus.NotPublished,
                  }),
                );

                const signal: ReloadSignal = {
                  shiftId: null,
                  shouldGoBack: false,
                  shouldReload: true,
                };
                subscription?.unsubscribe();
                modal.destroy(signal);
              } catch {
                subscription?.unsubscribe();
                this.nzMessageService.error(
                  this.translate.instant('general.something_went_wrong'),
                );
              }
            }
            subscription?.unsubscribe();
            modal.destroy();
          },
        },
      ],
    });
    modal.afterClose.pipe(take(1)).subscribe(() => this.resetModalStates());
    return modal;
  }

  private resetModalStates() {
    this.store.dispatch([
      new ToggleRenderTimelineInModal(),
      new ResetTimelineInteractionsState(),
      new ResetShiftAssignmentModalState(),
      new ResetShiftTimelineWidth(),
    ]);
    this.modalInteractionService.resetModalState();
  }
}
